import { ArrowDownRight, Sparkles } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";

export default function Hero() {
    const { ref: textRef, visible: textVisible } = useReveal();
    const { ref: imgRef, visible: imgVisible } = useReveal();

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pt-32 md:pt-36 pb-20 md:pb-28 overflow-hidden"
        >
            {/* Decorative blur shapes */}
            <div className="blur-shape w-[420px] h-[420px] bg-[#D8B9A7]/60 -top-20 -left-32" />
            <div className="blur-shape w-[320px] h-[320px] bg-[#C6A76A]/40 top-1/2 -right-20" />

            <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
                    {/* Left text column */}
                    <div
                        ref={textRef}
                        className={`lg:col-span-7 reveal ${textVisible ? "is-visible" : ""}`}
                    >
                        <div
                            data-testid="hero-eyebrow"
                            className="inline-flex items-center gap-3 mb-8 pl-4 border-l-2 border-[#C6A76A]"
                        >
                            <Sparkles size={14} strokeWidth={1.5} className="text-[#C6A76A]" />
                            <span className="eyebrow">Consultoría de imagen y personal branding</span>
                        </div>

                        <h1
                            data-testid="hero-headline"
                            className="font-display text-[42px] leading-[1.04] sm:text-[58px] md:text-[72px] lg:text-[88px] xl:text-[104px] text-[#1E1E1E] tracking-[-0.02em] text-balance"
                        >
                            Transforma tu{" "}
                            <span className="font-italic-display text-[#8C7C72]">imagen</span>{" "}
                            para proyectar tu{" "}
                            <span className="relative inline-block">
                                <span className="relative z-10">mejor versión</span>
                                <span className="absolute left-0 right-0 bottom-1 h-3 md:h-4 bg-[#C6A76A]/40 -z-0" aria-hidden="true" />
                            </span>
                        </h1>

                        <p
                            data-testid="hero-subheadline"
                            className="mt-8 text-base sm:text-lg text-[#1E1E1E]/75 max-w-xl leading-relaxed font-light"
                        >
                            Imagen estratégica para proyectar seguridad, coherencia y presencia en tu vida personal, profesional y digital.
                        </p>

                        <ul
                            data-testid="hero-bullets"
                            className="mt-10 grid gap-4 max-w-lg"
                        >
                            {[
                                "Alinea tu imagen con tus objetivos",
                                "Gana seguridad y presencia",
                                "Aprende a usar lo que ya tienes",
                            ].map((b, i) => (
                                <li
                                    key={b}
                                    data-testid={`hero-bullet-${i}`}
                                    className="flex items-start gap-4"
                                >
                                    <span className="mt-2 inline-block w-8 h-px bg-[#1E1E1E]" />
                                    <span className="text-[15px] text-[#1E1E1E] font-light tracking-wide">
                                        {b}
                                    </span>
                                </li>
                            ))}
                        </ul>

                        <div className="mt-12 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                            <a
                                href="#curso"
                                data-testid="hero-cta-primary"
                                className="btn-primary group"
                            >
                                <span>🎁 Descarga gratis el curso</span>
                                <ArrowDownRight
                                    size={18}
                                    className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                                />
                            </a>
                            <a
                                href="#contacto"
                                data-testid="hero-cta-secondary"
                                className="text-[12px] tracking-[0.2em] uppercase text-[#1E1E1E]/70 hover:text-[#1E1E1E] underline underline-offset-8 decoration-[#C6A76A] decoration-2"
                            >
                                o agenda tu diagnóstico
                            </a>
                        </div>

                        <p
                            data-testid="hero-course-title"
                            className="mt-6 font-italic-display text-[#8C7C72] text-sm"
                        >
                            “La imagen vende y vende bien”
                        </p>
                    </div>

                    {/* Right image column */}
                    <div
                        ref={imgRef}
                        className={`lg:col-span-5 relative reveal ${imgVisible ? "is-visible" : ""}`}
                    >
                        <div className="relative aspect-[3/4] w-full max-w-[460px] mx-auto lg:ml-auto">
                            <div className="relative w-full h-full overflow-hidden">
                                <img
                                    src="https://customer-assets.emergentagent.com/job_hewser-minimal/artifacts/w5a4lcz3_Ariadna-2.webp"
                                    alt="Ariadna Padilla — Hewser Personal Branding"
                                    className="editorial-img w-full h-full object-cover"
                                />
                            </div>
                            <div className="absolute -bottom-6 left-4 md:left-auto md:-left-10 bg-[#F7F3EE] p-5 md:p-7 max-w-[240px] shadow-[0_24px_60px_-20px_rgba(30,30,30,0.25)] z-10">
                                <div className="text-[10px] tracking-[0.32em] uppercase text-[#8C7C72]">
                                    Hewser ®
                                </div>
                                <div className="mt-2 font-display italic text-[18px] leading-snug text-[#1E1E1E]">
                                    Imagen con propósito.
                                </div>
                            </div>
                            <div className="absolute -top-4 -right-4 w-24 h-24 border border-[#C6A76A] -z-0 hidden md:block" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
